import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/supabase-auth";
import { useProfile } from "@/hooks/useProfile";
import { useEffect } from "react";

/**
 * Hook to manage query cache lifecycle based on authentication and organization state
 * Clears cache when user logs out or switches organizations to prevent data leakage
 */
export function useQueryCacheManager() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const { profile } = useProfile();

  useEffect(() => {
    // Clear all cached data when user logs out
    if (!session) {
      queryClient.clear();
    }
  }, [session, queryClient]);

  useEffect(() => {
    // Clear organization-specific cache when organization changes
    // This ensures no cross-tenant data leakage when switching orgs
    const organizationId = profile?.organization_id;
    
    if (organizationId) {
      // Clear all queries that don't match the current organization
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          if (Array.isArray(queryKey) && queryKey.length >= 2) {
            // Clear old org queries that don't match current org
            if (queryKey[0] === "org" && queryKey[1] !== organizationId) {
              return true;
            }
            // Also clear user-scoped queries when user has org context
            if (queryKey[0] === "user") {
              return true;
            }
          }
          return false;
        }
      });
    }
  }, [profile?.organization_id, queryClient]);

  const clearUserCache = (userId?: string) => {
    if (userId) {
      // Clear all queries that start with this user's scope
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 queryKey.length >= 2 && 
                 queryKey[0] === "user" && 
                 queryKey[1] === userId;
        }
      });
    } else {
      // Clear all user-scoped data
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === "user";
        }
      });
    }
  };

  const clearOrganizationCache = (organizationId?: string) => {
    if (organizationId) {
      // Clear organization-specific cached data
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 queryKey.length >= 2 && 
                 queryKey[0] === "org" && 
                 queryKey[1] === organizationId;
        }
      });
    } else {
      // Clear all organization-scoped data
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === "org";
        }
      });
    }
  };

  const clearAllCaches = () => {
    queryClient.clear();
  };

  const switchToOrganizationContext = (newOrganizationId: string) => {
    // Clear old organization data and user data when switching to org context
    clearOrganizationCache();
    clearUserCache();
  };

  const switchToUserContext = () => {
    // Clear organization data when switching back to user context
    clearOrganizationCache();
  };

  return {
    clearUserCache,
    clearOrganizationCache,
    clearAllCaches,
    switchToOrganizationContext,
    switchToUserContext
  };
}